import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Form } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Step } from "./constants";
import InitialStep from "./initial-step";
import RunningHoursStep from "./running-hours-step";
import NotesStep from "./notes-step";
import { MaintenanceScheduleType, MaintenanceTask } from "@/lib/types";

export type ExecutionEntryFormValues = {
  executionDate: Date;
  runningHoursAtExecution?: number;
  notes?: string;
  files: { id: string; url: string }[] | File[];
};

export const getExecutionEntryFormSchema = (
  scheduleType?: MaintenanceScheduleType
) => {
  const requiresRunningHours =
    scheduleType !== "NonRecurringDate" && scheduleType !== "RecurringCalendar";

  return z.object({
    executionDate: z.date({
      required_error: "Execution date is required",
    }),
    runningHoursAtExecution: requiresRunningHours
      ? z.number({
          required_error: "Running hours is required",
          invalid_type_error: "Running hours must be a number",
        })
      : z.number().optional(),
    notes: z.string().optional(),
    files: z.array(z.any()).optional(),
  });
};

const ExecutionEntryForm = ({
  title,
  open,
  setOpen,
  form,
  step,
  setStep,
  defaultValues,
  onSubmit,
  isSubmitting,
  scheduleType,
  task,
  vesselId,
  onTaskUpdated,
}: {
  title?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  form: UseFormReturn<ExecutionEntryFormValues>;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  defaultValues: Partial<ExecutionEntryFormValues>;
  onSubmit: (values: ExecutionEntryFormValues) => void;
  /** True while a create/update execution-entry request is in flight; disables Save to prevent duplicate submissions. */
  isSubmitting?: boolean;
  scheduleType?: MaintenanceScheduleType;
  task?: MaintenanceTask;
  vesselId?: string;
  onTaskUpdated?: () => void;
}) => {
  const { t } = useTranslation();
  const requiresRunningHours =
    scheduleType !== "NonRecurringDate" && scheduleType !== "RecurringCalendar";

  const getStep = () => {
    switch (step) {
      case Step.INITIAL:
        return (
          <InitialStep
            title={title}
            form={form}
            setStep={setStep}
            requiresRunningHours={requiresRunningHours}
            task={task}
            vesselId={vesselId}
            onTaskUpdated={onTaskUpdated}
            isSubmitting={isSubmitting}
          />
        );
      case Step.RUNNING_HOURS:
        return <RunningHoursStep form={form} setStep={setStep} />;
      case Step.NOTES:
        return <NotesStep form={form} setStep={setStep} />;
      default:
        return null;
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          setStep(Step.INITIAL);
          form.reset({
            ...defaultValues,
          });
        }
      }}
    >
      <DrawerContent
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        data-testid="execution-entry-form-drawer"
      >
        <DrawerHeader className="hidden">
          <DrawerTitle>{title ?? t("title.add_execution_entry")}</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription />
        <Form {...form}>
          <form
            data-testid="execution-entry-form"
            onSubmit={(e) => {
              form.handleSubmit(onSubmit, (errors) => {
                console.warn(errors);
              })(e);
            }}
          >
            {getStep()}
          </form>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

export default ExecutionEntryForm;
